import { InitialsAvatar } from '@alison/ui';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatWhole, initialsFrom } from '../../lib/format';
import { keyList } from '../media/overview/MediaBoardSkeleton';
import { ErrorState } from '../media/shared/ErrorState';
import { MediaCard } from '../media/shared/MediaCard';
import { NoData } from '../media/shared/NoData';
import type { CompetitionTopMetricsData } from './creatives/data/api';

const MAX_MARKETS = 3;
const MAX_SHARE_COMPETITORS = 5;
const AVATAR_SIZE = 22;
const CARD_COUNT = 4;
// Floors every card (loaded or skeleton) to the same height so the switch between them never
// shifts the page.
const CARD_MIN_HEIGHT = 108;

interface CompetitionTopMetricsPanelProps {
  data: CompetitionTopMetricsData | undefined;
  isLoading?: boolean;
  isError?: boolean;
}

interface ShareBarSegment {
  key: string;
  widthPercent: number;
  tooltip: string;
}

function cardSx(theme: Theme) {
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 1,
    py: 2,
    px: 3,
    minWidth: 0,
    minHeight: CARD_MIN_HEIGHT,
    boxShadow: 'none',
    transition: theme.transitions.create(['background-color', 'transform', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] },
  };
}

function barRadius(theme: Theme): string {
  return `${theme.radius.sm}px`;
}

function CardShell({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <MediaCard sx={cardSx}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      {children}
    </MediaCard>
  );
}

function CardSkeleton(): ReactNode {
  return (
    <MediaCard sx={cardSx}>
      <Skeleton variant="text" width="50%" />
      <Skeleton variant="text" width="80%" height={32} />
    </MediaCard>
  );
}

function CompetitionTopMetricsSkeleton(): ReactNode {
  return (
    <Box
      data-test="competition-top-metrics-loading"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(196px, 1fr))',
        gap: 2,
      }}
    >
      {keyList(CARD_COUNT, 'top-metric').map((key) => (
        <CardSkeleton key={key} />
      ))}
    </Box>
  );
}

function titleCase(value: string): string {
  return value.length === 0 ? value : value[0] + value.slice(1).toLowerCase();
}

function StackedShareBar({ segments }: { segments: ShareBarSegment[] }): ReactNode {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        gap: 0.4,
        height: 8,
        borderRadius: barRadius(theme),
        overflow: 'hidden',
      })}
    >
      {segments.map((segment, index) => (
        <Tooltip key={segment.key} title={segment.tooltip}>
          <Box
            sx={(theme) => ({
              width: `${segment.widthPercent}%`,
              bgcolor: theme.palette.chartSeries[index % theme.palette.chartSeries.length],
            })}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

function TotalCreativesCard({ data }: { data: CompetitionTopMetricsData }): ReactNode {
  return (
    <CardShell label="Total creatives">
      {data.totalCreatives === 0 ? (
        <NoData />
      ) : (
        <>
          <Typography variant="h1">{formatWhole(data.totalCreatives)}</Typography>
          <Typography color="text.secondary">
            across {formatWhole(data.competitors.length)} competitors
          </Typography>
        </>
      )}
    </CardShell>
  );
}

function FormatMixCard({ data }: { data: CompetitionTopMetricsData }): ReactNode {
  const top = [...data.formatMix].sort((a, b) => b.creatives - a.creatives).slice(0, 2);
  const total = top.reduce((sum, entry) => sum + entry.creatives, 0);

  return (
    <CardShell label="Format mix">
      {total === 0 ? (
        <NoData />
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {top.map((entry) => (
              <Typography key={entry.mediaType} variant="h2">
                {Math.round((entry.creatives / total) * 100)}%
              </Typography>
            ))}
          </Box>
          <StackedShareBar
            segments={top.map((entry) => ({
              key: entry.mediaType,
              widthPercent: (entry.creatives / total) * 100,
              tooltip: `${titleCase(entry.mediaType)}: ${formatWhole(entry.creatives)} (${Math.round((entry.creatives / total) * 100)}%)`,
            }))}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {top.map((entry) => (
              <Typography key={entry.mediaType} color="text.secondary">
                {titleCase(entry.mediaType)}
              </Typography>
            ))}
          </Box>
        </>
      )}
    </CardShell>
  );
}

function TopMarketsCard({ data }: { data: CompetitionTopMetricsData }): ReactNode {
  const top = [...data.markets].sort((a, b) => b.creatives - a.creatives).slice(0, MAX_MARKETS);
  const max = Math.max(...top.map((entry) => entry.creatives), 1);

  return (
    <CardShell label="Top markets">
      {top.length === 0 ? (
        <NoData />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {top.map((entry) => (
            <Box key={entry.country} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ width: 24, flexShrink: 0 }}>{entry.country}</Typography>
              <Tooltip title={`${entry.country}: ${formatWhole(entry.creatives)} creatives`}>
                <Box
                  sx={(theme) => ({
                    flex: 1,
                    height: 8,
                    borderRadius: barRadius(theme),
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      width: `${(entry.creatives / max) * 100}%`,
                      height: '100%',
                      borderRadius: barRadius(theme),
                      bgcolor: theme.palette.primary.main,
                    })}
                  />
                </Box>
              </Tooltip>
              <Typography color="text.secondary" sx={{ flexShrink: 0 }}>
                {formatWhole(entry.creatives)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </CardShell>
  );
}

function CompetitorShareCard({ data }: { data: CompetitionTopMetricsData }): ReactNode {
  const top = [...data.competitors]
    .sort((a, b) => b.creatives - a.creatives)
    .slice(0, MAX_SHARE_COMPETITORS);
  const total = top.reduce((sum, competitor) => sum + competitor.creatives, 0);

  function sharePercent(competitor: CompetitionTopMetricsData['competitors'][number]): string {
    return `${Math.round((competitor.creatives / total) * 100)}%`;
  }

  function shareTooltip(competitor: CompetitionTopMetricsData['competitors'][number]): string {
    return `${competitor.competitorName}: ${formatWhole(competitor.creatives)} (${sharePercent(competitor)})`;
  }

  return (
    <CardShell label="Competitor share">
      {total === 0 ? (
        <NoData />
      ) : (
        <>
          <StackedShareBar
            segments={top.map((competitor) => ({
              key: competitor.competitorId,
              widthPercent: (competitor.creatives / total) * 100,
              tooltip: shareTooltip(competitor),
            }))}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {top.map((competitor) => (
              <Box
                key={competitor.competitorId}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
              >
                <Tooltip title={shareTooltip(competitor)}>
                  <InitialsAvatar
                    variant="rounded"
                    size={AVATAR_SIZE}
                    initials={initialsFrom(competitor.competitorName) ?? ''}
                    src={competitor.logoUrl ?? undefined}
                  />
                </Tooltip>
                <Typography color="text.secondary">{sharePercent(competitor)}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </CardShell>
  );
}

export function CompetitionTopMetricsPanel({
  data,
  isLoading = false,
  isError = false,
}: CompetitionTopMetricsPanelProps): ReactNode {
  if (data === undefined && !isLoading && !isError) return null;

  return (
    <Box sx={{ bgcolor: 'background.paper', px: 6, pt: 2, pb: 1 }}>
      {isError ? (
        <ErrorState message="Couldn't load these metrics." />
      ) : data === undefined ? (
        <CompetitionTopMetricsSkeleton />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(196px, 1fr))',
            gap: 2,
          }}
        >
          <TotalCreativesCard data={data} />
          <FormatMixCard data={data} />
          {/* Hidden when no creative in scope has a country. */}
          {data.markets.length > 0 && <TopMarketsCard data={data} />}
          <CompetitorShareCard data={data} />
        </Box>
      )}
    </Box>
  );
}
